/**
 * Wall collision detection for Voronoi-based mazes.
 *
 * Extracts wall edges (shared edges between non-connected regions)
 * and contour edges (silhouette boundary) from VoronoiMazeData,
 * then provides line-segment intersection checks for path drawing.
 */

import type { Position, VoronoiMazeData } from '../types/maze-types';

/**
 * A wall edge is a pair of positions defining a line segment.
 */
export type VoronoiWallEdge = [Position, Position];

/**
 * Create a canonical key for an edge (order-independent).
 */
function edgeKey(a: Position, b: Position): string {
  const ka = `${Math.round(a.x * 10)},${Math.round(a.y * 10)}`;
  const kb = `${Math.round(b.x * 10)},${Math.round(b.y * 10)}`;
  return ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
}

/**
 * Extract all wall edges (walls + contour) from Voronoi maze data.
 * These are the edges the player cannot cross while drawing a path.
 */
export function extractVoronoiWalls(mazeData: VoronoiMazeData): VoronoiWallEdge[] {
  const neighborSet = new Set<string>();
  const edgeMap = new Map<string, { v1: Position; v2: Position; regionIds: number[] }>();

  // Build neighbor pair set (passages — no wall)
  for (const region of mazeData.regions) {
    for (const nid of region.neighbors) {
      const a = Math.min(region.id, nid);
      const b = Math.max(region.id, nid);
      neighborSet.add(`${a},${b}`);
    }
  }

  // Collect all edges from all regions
  for (const region of mazeData.regions) {
    const verts = region.vertices;
    for (let i = 0; i < verts.length; i++) {
      const v1 = verts[i];
      const v2 = verts[(i + 1) % verts.length];
      const key = edgeKey(v1, v2);

      const existing = edgeMap.get(key);
      if (existing) {
        if (!existing.regionIds.includes(region.id)) {
          existing.regionIds.push(region.id);
        }
      } else {
        edgeMap.set(key, { v1, v2, regionIds: [region.id] });
      }
    }
  }

  const walls: VoronoiWallEdge[] = [];

  for (const info of edgeMap.values()) {
    if (info.regionIds.length === 1) {
      // Boundary edge — part of contour (also a wall)
      walls.push([info.v1, info.v2]);
    } else if (info.regionIds.length === 2) {
      const a = Math.min(info.regionIds[0], info.regionIds[1]);
      const b = Math.max(info.regionIds[0], info.regionIds[1]);
      if (!neighborSet.has(`${a},${b}`)) {
        // Shared edge with no passage — wall
        walls.push([info.v1, info.v2]);
      }
    }
  }

  return walls;
}

/**
 * Check if a line segment (from → to) crosses any Voronoi wall edge.
 * Uses standard line-segment intersection test.
 */
export function isPathCrossingVoronoiWall(
  from: Position,
  to: Position,
  walls: VoronoiWallEdge[],
): boolean {
  for (const [w1, w2] of walls) {
    if (segmentsIntersect(from, to, w1, w2)) {
      return true;
    }
  }
  return false;
}

/**
 * Determine if two line segments (p1-p2 and p3-p4) intersect.
 * Uses the cross-product orientation test.
 */
function segmentsIntersect(
  p1: Position,
  p2: Position,
  p3: Position,
  p4: Position,
): boolean {
  const d1 = direction(p3, p4, p1);
  const d2 = direction(p3, p4, p2);
  const d3 = direction(p1, p2, p3);
  const d4 = direction(p1, p2, p4);

  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
      ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
    return true;
  }

  if (d1 === 0 && onSegment(p3, p4, p1)) return true;
  if (d2 === 0 && onSegment(p3, p4, p2)) return true;
  if (d3 === 0 && onSegment(p1, p2, p3)) return true;
  if (d4 === 0 && onSegment(p1, p2, p4)) return true;

  return false;
}

function direction(pi: Position, pj: Position, pk: Position): number {
  return (pk.x - pi.x) * (pj.y - pi.y) - (pj.x - pi.x) * (pk.y - pi.y);
}

function onSegment(pi: Position, pj: Position, pk: Position): boolean {
  return (
    Math.min(pi.x, pj.x) <= pk.x &&
    pk.x <= Math.max(pi.x, pj.x) &&
    Math.min(pi.y, pj.y) <= pk.y &&
    pk.y <= Math.max(pi.y, pj.y)
  );
}
